import _ from 'lodash';
//reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getCountries } from 'state/reducers/global.reducers';
import { getNewBeneficiary } from 'state/reducers/beneficiary.reducers';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
//theme
import { LayoutColumn } from 'theme/layouts';
import { NavigationBack } from 'theme/elements/Headers';
import { PageHeaderCard } from 'theme/elements/Cards';
//components
import { ChipType, CustomStatusChip } from 'theme/elements/Chips';
import { FormButton, GeneralButton } from 'theme/elements/Buttons';
//MUI
import { Typography, Stack, TextField, Button, Box } from '@mui/material';
import Add from '@mui/icons-material/Add';
//constants
import {
	Beneficiary,
	BeneficiaryId,
	BeneficiaryStatus,
	PendingBeneficiaryStatus,
} from 'constants/beneficiary.constants';
import { pagesEnum } from 'constants/ui.constants';
import { useNavigate } from 'react-router-dom';
import BeneficiaryNameWithFlag from './BeneficiaryNameWithFlag';
import { updateBeneficiaryNote } from 'services/beneficiary.services';
import { getUserPermission } from 'state/reducers/user.reducers';
import { UserPermission } from 'constants/user.constants';
import { getMobile } from 'state/reducers/ui.reducers';

export default function Header() {
	const t = useAppSelector(selectTranslations);
	const beneficiary: Partial<Beneficiary> = useAppSelector(getNewBeneficiary);
	const navigate = useNavigate();
	const countries = _.keyBy(useAppSelector(getCountries), 'id');
	const dispatch = useAppDispatch();
	const userPermission = useAppSelector(getUserPermission);
	const isMobile = useAppSelector(getMobile);
	const prefixFields = `page.beneficiary.profile.header.`;

	return (
		<PageHeaderCard>
			<Stack spacing={2} direction={{ sm: 'row', xs: 'column' }}>
				<LayoutColumn width={isMobile ? '100%' : '75%'}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
						<Stack
							direction="row"
							justifyContent={{ xs: 'space-between' }}
							width={{ xs: '100%', sm: 'initial' }}
							spacing={{ sm: 2, xs: 0 }}
						>
							<Stack direction="row" spacing={{ sm: 2, xs: 0 }}>
								<Typography display={{ sm: 'block', xs: 'none' }} variant="h4">
									<NavigationBack />
								</Typography>
								<BeneficiaryNameWithFlag beneficiary={beneficiary} />
							</Stack>
							{_.includes(
								[BeneficiaryStatus.Approved, BeneficiaryStatus.ApprovedWithRisk],
								beneficiary.status
							) ? (
								<CustomStatusChip
									type={ChipType.APPROVED}
									label={t(`${prefixFields}status.${_.snakeCase(beneficiary.status)}.text`)}
								/>
							) : (
								<CustomStatusChip
									type={
										_.includes(PendingBeneficiaryStatus, beneficiary.status)
											? ChipType.NATURAL
											: ChipType.ERROR
									}
									label={t(`${prefixFields}status.${_.snakeCase(beneficiary.status)}.text`)}
								/>
							)}
						</Stack>
						{!isMobile && (
							<PayNow isMobile={isMobile} beneficiary={beneficiary} userPermission={userPermission} />
						)}
						{_.isEqual(userPermission, UserPermission.SIGNER) &&
							_.includes([BeneficiaryStatus.Suspended], beneficiary.status) && (
								<FormButton>
									<GeneralButton>
										<Button
											onClick={() => {
												navigate(`/${pagesEnum.BENEFICIARY}/edit/${beneficiary.id}`);
											}}
											variant="contained"
										>
											{t(`${prefixFields}activate_beneficiary_button.text`)}
										</Button>
									</GeneralButton>
								</FormButton>
							)}
					</Box>
				</LayoutColumn>
				<LayoutColumn width={isMobile ? '100%' : '25%'}>
					{_.includes([UserPermission.SIGNER, UserPermission.OPERATIONER], userPermission) && (
						<TextField
							placeholder={t(`${prefixFields}add_note.text`)}
							onBlur={(event) => {
								const new_value = _.trim(event.target.value);
								if (new_value !== '' && !_.isEqual(new_value, beneficiary.note) && beneficiary?.id) {
									dispatch(updateBeneficiaryNote(beneficiary.id, new_value));
								}
							}}
							defaultValue={beneficiary.note || ''}
							multiline
							rows={4.15}
							sx={{
								padding: 0,
								width: '100%',
								backgroundColor: '#fff',
								//border: 'solid 1px ' + theme.palette.root.darkGrayed,
								borderRadius: '8px',
								'& .MuiInputBase-root': {
									padding: '12px',
								},
							}}
						/>
					)}
				</LayoutColumn>
				{isMobile && <PayNow isMobile={isMobile} beneficiary={beneficiary} userPermission={userPermission} />}
			</Stack>
		</PageHeaderCard>
	);
}

export function PayNow(props: {
	isMobile: boolean;
	userPermission: UserPermission;
	beneficiary: Partial<Beneficiary>;
}) {
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const imageUrl = useAppSelector(selectImageUrl);
	const prefixFields = `page.beneficiary.profile.header.`;
	return (
		<>
			{_.includes([UserPermission.SIGNER, UserPermission.OPERATIONER], props.userPermission) &&
				_.includes(
					[BeneficiaryStatus.Approved, BeneficiaryStatus.ApprovedWithRisk],
					props.beneficiary.status
				) && (
					<FormButton>
						<GeneralButton>
							<Button
								fullWidth={props.isMobile}
								onClick={() => {
									navigate(`/${pagesEnum.PAY}/new/${_.get(props.beneficiary, BeneficiaryId)}`);
								}}
								variant="contained"
								startIcon={
									imageUrl(`${prefixFields}pay_now_button.icon`) ? (
										<img src={imageUrl(`${prefixFields}pay_now_button.icon`)} height={'24px'} />
									) : (
										<Add sx={{ svg: { fontSize: '24px' } }} />
									)
								}
							>
								{t(`${prefixFields}pay_now_button.text`)}
							</Button>
						</GeneralButton>
					</FormButton>
				)}
		</>
	);
}
